import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { Link, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import CheckValid from "../../Common/CheckValid";
import { WARRANTY_SUBSCRIPTION_URL } from "../../Shared/constant";
import { WARRANTY_PERMISSION, hasWarrantyPermission } from "../common/warrantyAccess";
import { Loading, EmptyState, ErrorState } from "../common/WarrantyStates";
import WarrantyPage from "../common/WarrantyPage";
import {
  downloadSettlementCsv,
  getPartnerSettlement,
  listSettlementPartners,
} from "../common/warrantyApi";
import { useWarrantyNav } from "../common/useWarrantyNav";
import { saveFile } from "../common/saveFile";
import { formatDateTime, formatMinor } from "../common/format";
import {
  benefitKindLabel,
  partnerStateLabel,
  settlementLineTypeLabel,
  settlementRefundKindLabel,
} from "../common/opsLabels";
import "../warranty.css";

// India only for now, so the month is India's month: a statement for "this month" must not move
// a day early or late because the browser runs in another time zone.
const MARKET_TIME_ZONE = "Asia/Kolkata";
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const marketMonthNow = () => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: MARKET_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  return `${year}-${month}`;
};

const previousMonth = (yearMonth) => {
  const [year, month] = yearMonth.split("-").map(Number);
  return month === 1 ? `${year - 1}-12` : `${year}-${String(month - 1).padStart(2, "0")}`;
};

const monthLabel = (yearMonth) => {
  const parts = /^(\d{4})-(\d{2})$/.exec(yearMonth || "");
  return parts ? `${MONTH_NAMES[Number(parts[2]) - 1]} ${parts[1]}` : yearMonth || "-";
};

const shortId = (id) => (typeof id === "string" ? `${id.slice(0, 8)}...${id.slice(-4)}` : "-");

// The month rule lives in CheckValid (warranty_month); this only asks it for the sentence.
const monthError = (value, max) => {
  let message = "";
  CheckValid(value, {
    type: "warranty_month",
    max,
    error: (m) => {
      message = m;
    },
  });
  return message;
};

// The partner comes from the list and the month passed CheckValid, so a 400 here is the service's
// month rule (its refusal text names no value). The service sends VALIDATION_FAILED; the contract
// wrote VALIDATION_ERROR, so both read the same. A month over the service's line limit fails the
// page as well as the file.
const statementMessage = (result) => {
  if (result.httpStatus === 400 && (result.code === "VALIDATION_FAILED" || result.code === "VALIDATION_ERROR")) {
    return "The warranty service did not accept this partner and month. The month cannot be after the current month in India.";
  }
  if (result.code === "SETTLEMENT_TOO_LARGE") {
    return "This month has too many lines to show here. Ask engineering for the statement.";
  }
  return result.message;
};

const NO_STATEMENT = {
  status: "idle",
  meta: null,
  lines: [],
  nextCursor: null,
  query: null,
  message: "",
  loadingMore: false,
  moreError: "",
};
const NO_DOWNLOAD = { inFlight: false, message: "", note: "" };

const PartnerSettlement = () => {
  const [params, setParams] = useSearchParams();
  const { selfInfo, accessToken } = useSelector((state) => state.adminReducers);
  const allowed = hasWarrantyPermission(selfInfo, WARRANTY_PERMISSION.OPS);
  useWarrantyNav(allowed);

  const maxMonth = marketMonthNow();
  const fromUrl = useRef({ partner: params.get("partner") || "", month: params.get("month") || "" });
  const [partners, setPartners] = useState({ status: "loading", items: [], message: "" });
  const [partnersReload, setPartnersReload] = useState(0);
  const [partnerKey, setPartnerKey] = useState("");
  const [month, setMonth] = useState(() =>
    monthError(fromUrl.current.month, maxMonth) === "" ? fromUrl.current.month : previousMonth(maxMonth)
  );
  const [fieldError, setFieldError] = useState("");
  const [statement, setStatement] = useState(NO_STATEMENT);
  const [download, setDownload] = useState(NO_DOWNLOAD);
  const statementRef = useRef(NO_STATEMENT);
  const seq = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // The paging below reads the statement it extends from this ref, never a stale render.
  const put = useCallback((next) => {
    statementRef.current = typeof next === "function" ? next(statementRef.current) : next;
    setStatement(statementRef.current);
  }, []);

  const load = useCallback(
    async (query) => {
      const mine = ++seq.current;
      setDownload(NO_DOWNLOAD);
      put({ ...NO_STATEMENT, status: "loading", query });
      const result = await getPartnerSettlement(accessToken, query);
      if (!mounted.current || mine !== seq.current) {
        return;
      }
      if (result.ok && result.data && typeof result.data === "object") {
        const { lines, nextCursor, ...meta } = result.data;
        put({
          ...NO_STATEMENT,
          status: "ready",
          meta,
          lines: Array.isArray(lines) ? lines : [],
          nextCursor: nextCursor || null,
          query,
        });
      } else {
        put({ ...NO_STATEMENT, status: "error", query, message: statementMessage(result) });
      }
    },
    [accessToken, put]
  );

  useEffect(() => {
    if (!allowed) {
      return undefined;
    }
    let live = true;
    setPartners({ status: "loading", items: [], message: "" });
    (async () => {
      const result = await listSettlementPartners(accessToken);
      if (!live) {
        return;
      }
      if (result.ok) {
        const items = Array.isArray(result.data?.partners)
          ? result.data.partners.filter((item) => typeof item?.partnerKey === "string")
          : [];
        setPartners({ status: "ready", items, message: "" });
      } else {
        // A 404 here is the service's settlement flag being off, not a missing item.
        setPartners({
          status: "error",
          items: [],
          message: result.notFound ? "Partner settlement is not switched on." : result.message,
        });
      }
    })();
    return () => {
      live = false;
    };
  }, [allowed, accessToken, partnersReload]);

  // The partner in the link when it is one of the list, else the first. A link that names both a
  // partner and a valid month opens that statement straight away, once.
  useEffect(() => {
    if (partners.status !== "ready" || partners.items.length === 0 || partnerKey !== "") {
      return;
    }
    const keys = partners.items.map((item) => item.partnerKey);
    const linked = keys.includes(fromUrl.current.partner) ? fromUrl.current.partner : "";
    setPartnerKey(linked || keys[0]);
    if (linked && monthError(fromUrl.current.month, maxMonth) === "") {
      load({ partnerKey: linked, month: fromUrl.current.month });
    }
  }, [partners, partnerKey, maxMonth, load]);

  const submit = () => {
    if (statement.status === "loading") {
      return;
    }
    const message = partnerKey === "" ? "Choose a partner" : monthError(month, maxMonth);
    setFieldError(message);
    if (message !== "") {
      return;
    }
    setParams({ partner: partnerKey, month: month.trim() }, { replace: true });
    load({ partnerKey, month: month.trim() });
  };

  const loadMore = async () => {
    const current = statementRef.current;
    if (!current.nextCursor || current.loadingMore) {
      return;
    }
    const mine = seq.current;
    put((prev) => ({ ...prev, loadingMore: true, moreError: "" }));
    const result = await getPartnerSettlement(accessToken, {
      ...current.query,
      cursor: current.nextCursor,
    });
    if (!mounted.current || mine !== seq.current) {
      return;
    }
    if (!result.ok) {
      put((prev) => ({ ...prev, loadingMore: false, moreError: result.message }));
    } else if (result.data?.contentSha256 !== current.meta?.contentSha256) {
      // The hash covers every line of the month: a different one means a line changed between
      // the pages, so the rows on screen and the next page would not add up.
      put((prev) => ({
        ...prev,
        loadingMore: false,
        moreError: "The statement changed while you were reading it. Load it again to see it whole.",
      }));
    } else {
      put((prev) => ({
        ...prev,
        loadingMore: false,
        lines: [...prev.lines, ...(Array.isArray(result.data.lines) ? result.data.lines : [])],
        nextCursor: result.data.nextCursor || null,
      }));
    }
  };

  const downloadCsv = async () => {
    const current = statementRef.current;
    if (current.status !== "ready" || download.inFlight) {
      return;
    }
    // A Load while the file is on its way replaces the statement: the old file is then not saved,
    // and no "Saved" note appears under a statement it does not belong to.
    const mine = seq.current;
    setDownload({ ...NO_DOWNLOAD, inFlight: true });
    const result = await downloadSettlementCsv(accessToken, current.query, current.meta?.statementId);
    if (!mounted.current || mine !== seq.current) {
      return;
    }
    if (!result.ok) {
      setDownload({ ...NO_DOWNLOAD, message: result.message });
      return;
    }
    saveFile(result.blob, result.filename);
    // The file's hash comes in a header the browser can read only when the OMS exposes it (CORS);
    // without it the comparison cannot be made, and the person is told so rather than left to assume.
    const expected = current.meta?.contentSha256;
    let note = `Saved ${result.filename}.`;
    if (result.contentSha256 === null || typeof expected !== "string") {
      note = `Saved ${result.filename}. Could not verify the file against the statement.`;
    } else if (result.contentSha256 !== expected) {
      note = `Saved ${result.filename}, but it was made from newer data than the statement on screen. Load the statement again to compare.`;
    }
    setDownload({ ...NO_DOWNLOAD, note });
  };

  const meta = statement.meta;
  const totals = meta?.totals || {};
  const currency = meta?.currency || "INR";
  // why (15 Oct 2026, Sachin): the vendor policies bought by hand settle under `manual_vendor`,
  // which has no insurer API and so reads as not set up; name it for what it is.
  const partnerLabel = (item) => {
    if (item.partnerKey === "manual_vendor") return "Vendor (bought by hand)";
    return item.configured === false ? `${item.partnerKey} (no longer set up)` : item.partnerKey;
  };

  return (
    <WarrantyPage
      title="Partner settlement"
      sub="What each insurance partner is owed for a month: the cover paid for, less the cover refunded."
      allowed={allowed}
      deniedTitle="You do not have access to partner settlement"
      deniedText="Ask an admin to give you the Warranty Operations permission."
    >
      {partners.status === "loading" && <Loading label="Loading the partners" />}
      {partners.status === "error" && (
        <ErrorState
          message={partners.message}
          onRetry={() => setPartnersReload((value) => value + 1)}
        />
      )}
      {partners.status === "ready" && partners.items.length === 0 && (
        <EmptyState title="No partner to settle with yet">
          <span>A partner appears here once it is set up for India or has issued a policy.</span>
        </EmptyState>
      )}

      {partners.status === "ready" && partners.items.length > 0 && (
        <>
          {/* noValidate: the browser's own month check would stop the submit with its own bubble
              before CheckValid can give the house sentence. */}
          <form
            className="formloginset warranty-lookup"
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
            autoComplete="off"
            noValidate
          >
            <Form.Group controlId="warrantySettlementPartner">
              <Form.Label>Partner</Form.Label>
              <Form.Select
                value={partnerKey}
                onChange={(event) => {
                  setPartnerKey(event.target.value);
                  setFieldError("");
                }}
              >
                {partners.items.map((item) => (
                  <option key={item.partnerKey} value={item.partnerKey}>
                    {partnerLabel(item)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group controlId="warrantySettlementMonth">
              <Form.Label>Month</Form.Label>
              <Form.Control
                type="month"
                value={month}
                min="2000-01"
                max={maxMonth}
                placeholder="2026-09"
                onChange={(event) => {
                  setMonth(event.target.value);
                  if (fieldError) {
                    setFieldError(monthError(event.target.value, maxMonth));
                  }
                }}
              />
            </Form.Group>
            <Button
              className="Addbtn warranty-lookup-btn"
              type="submit"
              disabled={statement.status === "loading"}
            >
              {statement.status === "loading" ? "Loading" : "Load"}
            </Button>
          </form>
          <div className="warranty-lookup-msg" aria-live="polite">
            {fieldError && (
              <span className="danger-colorset" role="alert">
                {fieldError}
              </span>
            )}
          </div>
        </>
      )}

      {statement.status === "idle" && partners.status === "ready" && partners.items.length > 0 && (
        <EmptyState title="Choose a partner and a month, then Load">
          <span>The month starts as the last complete month in India.</span>
        </EmptyState>
      )}
      {statement.status === "loading" && <Loading label="Loading the statement" />}
      {statement.status === "error" && (
        <ErrorState message={statement.message} onRetry={() => load(statement.query)} />
      )}

      {statement.status === "ready" && meta && (
        <>
          <section className="warranty-panel" aria-label="Statement totals">
            <h5>
              {statement.query.partnerKey}{" "}
              <span className="warranty-muted">
                {monthLabel(statement.query.month)}, {meta.timeZone || MARKET_TIME_ZONE} time, in{" "}
                {currency}
              </span>
            </h5>
            <table className="warranty-preview-table warranty-totals-table">
              <thead>
                <tr>
                  <th scope="col">Whole month</th>
                  <th scope="col" className="warranty-num">
                    Lines
                  </th>
                  <th scope="col" className="warranty-num">
                    Amount
                  </th>
                  <th scope="col" className="warranty-num">
                    Tax
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">Paid</th>
                  <td className="warranty-num">{totals.paidCount ?? "-"}</td>
                  <td className="warranty-num">{formatMinor(totals.paidAmountMinor, currency)}</td>
                  <td className="warranty-num">{formatMinor(totals.paidTaxMinor, currency)}</td>
                </tr>
                <tr>
                  <th scope="row">Refunded</th>
                  <td className="warranty-num">{totals.refundCount ?? "-"}</td>
                  <td className="warranty-num">{formatMinor(totals.refundedAmountMinor, currency)}</td>
                  <td className="warranty-num">{formatMinor(totals.refundedTaxMinor, currency)}</td>
                </tr>
                <tr className="is-net">
                  <th scope="row">Net</th>
                  <td className="warranty-num"></td>
                  <td className="warranty-num">{formatMinor(totals.netAmountMinor, currency)}</td>
                  <td className="warranty-num">{formatMinor(totals.netTaxMinor, currency)}</td>
                </tr>
              </tbody>
            </table>
            <dl className="warranty-facts">
              <dt>Made</dt>
              <dd>{formatDateTime(meta.generatedAt)}</dd>
              <dt>Statement</dt>
              <dd className="warranty-ref">{meta.statementId || "-"}</dd>
              <dt>Content hash</dt>
              <dd className="warranty-ref">{meta.contentSha256 || "-"}</dd>
            </dl>
            <div className="warranty-panel-actions">
              <Button className="Addbtn" onClick={downloadCsv} disabled={download.inFlight}>
                {download.inFlight ? "Downloading" : "Download CSV"}
              </Button>
              <span className="warranty-muted" aria-live="polite">
                {download.note}
              </span>
            </div>
            {download.message && (
              <div className="warranty-banner is-error" role="alert">
                <span>{download.message}</span>
              </div>
            )}
          </section>

          <section className="warranty-panel" aria-label="Statement lines">
            <h5>
              Lines{" "}
              <span className="warranty-muted">
                {statement.lines.length} shown{statement.nextCursor ? ", more to load" : ""}
              </span>
            </h5>
            {statement.lines.length === 0 ? (
              <EmptyState title={`No cover paid or refunded for ${statement.query.partnerKey} in ${monthLabel(statement.query.month)}`} />
            ) : (
              <>
                <div className="tableView warranty-ops-table">
                  <table className="table">
                    <thead>
                      <tr>
                        <th scope="col">Line</th>
                        <th scope="col">Frame</th>
                        <th scope="col">Subscription</th>
                        <th scope="col">Payment</th>
                        <th scope="col">Provider payment</th>
                        <th scope="col">Captured or refunded</th>
                        <th scope="col" className="warranty-num">
                          Amount
                        </th>
                        <th scope="col" className="warranty-num">
                          Tax
                        </th>
                        <th scope="col">Policy number</th>
                        <th scope="col">Policy state</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statement.lines.map((line, index) => (
                        <tr key={`${line.lineType}-${line.partnerPolicyId}-${line.refundKind || ""}-${index}`}>
                          <td>
                            <span className={`warranty-badge${line.lineType === "refund" ? " is-warning" : " is-live"}`}>
                              {settlementLineTypeLabel(line.lineType)}
                            </span>
                            <div className="warranty-muted">
                              {line.lineType === "refund"
                                ? settlementRefundKindLabel(line.refundKind)
                                : benefitKindLabel(line.benefitKind)}
                            </div>
                          </td>
                          <td className="warranty-nowrap">{line.frameMasked || "-"}</td>
                          <td className="warranty-nowrap">
                            {typeof line.subscriptionId === "string" ? (
                              <Link
                                to={`${WARRANTY_SUBSCRIPTION_URL}/${encodeURIComponent(line.subscriptionId)}`}
                                title={line.subscriptionId}
                              >
                                {shortId(line.subscriptionId)}
                              </Link>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td className="warranty-nowrap" title={line.paymentId || ""}>
                            {shortId(line.paymentId)}
                          </td>
                          <td className="warranty-ref">{line.providerPaymentId || "-"}</td>
                          <td>
                            {formatDateTime(line.lineType === "refund" ? line.refundedAt : line.capturedAt)}
                          </td>
                          <td className="warranty-num">{formatMinor(line.amountMinor, currency)}</td>
                          <td className="warranty-num">{formatMinor(line.taxMinor, currency)}</td>
                          <td>{line.policyNumber || "-"}</td>
                          <td>{partnerStateLabel(line.policyState)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="warranty-more">
                  {statement.moreError && (
                    <span className="warranty-field-msg is-error" role="alert">
                      {statement.moreError}
                    </span>
                  )}
                  {statement.nextCursor && (
                    <Button className="Addbtn" onClick={loadMore} disabled={statement.loadingMore}>
                      {statement.loadingMore ? "Loading" : "Load more"}
                    </Button>
                  )}
                </div>
              </>
            )}
          </section>
        </>
      )}
    </WarrantyPage>
  );
};

export default PartnerSettlement;
